import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AuthHero } from "@/components/auth/AuthHero";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [signupsOpen, setSignupsOpen] = useState(true);

  useEffect(() => {
    api.get("/auth/config").then(({ data }) => setSignupsOpen(data.signups_open !== false)).catch(() => {});
  }, []);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <AuthHero />

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up" data-testid="login-form">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Secure Access</p>
          <h2 className="font-heading text-3xl font-black tracking-tighter mb-8">Sign in</h2>

          {error && (
            <div className="mb-4 border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30] text-sm px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Email</Label>
              <Input data-testid="login-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-[#0A0A0A] border-border rounded-sm focus-visible:ring-[#007AFF]" placeholder="you@company.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Password</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link"
                  className="text-xs text-[#007AFF] hover:underline">Forgot password?</Link>
              </div>
              <Input data-testid="login-password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-[#0A0A0A] border-border rounded-sm focus-visible:ring-[#007AFF]" placeholder="••••••••" />
            </div>
          </div>

          <Button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full mt-6 bg-[#007AFF] hover:bg-[#0066d6] text-white rounded-sm gap-2 font-semibold h-11">
            {loading ? "Signing in…" : "Sign in"} <ArrowRight className="w-4 h-4" />
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button type="button" variant="outline" data-testid="google-login-btn" onClick={googleLogin}
            className="w-full rounded-sm gap-2 font-semibold h-11 border-border bg-[#0A0A0A] hover:bg-[#161616]">
            <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
              <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z" />
              <path fill="#FBBC05" d="M5.27 14.29A7.16 7.16 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a11.99 11.99 0 0 0 0 10.76l3.98-3.09z" />
              <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0A11.99 11.99 0 0 0 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
            </svg>
            Log in with Google
          </Button>
          <p className="text-[11px] text-muted-foreground mt-2 text-center" data-testid="login-age-notice">
            Adult-only service (18+). By continuing you confirm you are 18 years of age or older.
          </p>

          <p className="text-sm text-muted-foreground mt-6">
            {signupsOpen ? (
              <>No account?{" "}
              <Link to="/register" data-testid="go-register" className="text-[#007AFF] hover:underline">Create one</Link></>
            ) : (
              <span data-testid="login-private-notice">New sign-ups are closed right now — this is a private beta.</span>
            )}
          </p>
        </form>
      </div>
    </div>
  );
}
